import { datadogRum } from '@datadog/browser-rum';
import { Alert, AlertButton, toast } from '@side/pantry';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useAsyncFn } from 'react-use';
import { useTrackPlatformFeeAnalytics } from '$analytics/useTrackAnalytics/domains/platformFee';
import { TrackPlatformFeeEvent } from '$analytics/useTrackAnalytics/domains/platformFee/events';
import { useIdentity } from '$api-clients/hooks';
import {
  getPaymentCenterUrl,
  useFetchPlatformFee,
  usePlatformFeeTakeover,
} from './helpers';
import { PlatformFeeAlertStatus } from './types';

const PLATFORM_FEE_ALERT_CONTENTS: Partial<
  Record<
    PlatformFeeAlertStatus,
    {
      title: string;
      description: string;
      linkText: string;
    }
  >
> = {
  [PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD]: {
    title: 'Missing Payment Method',
    description: 'Add a payment method to pay the monthly platform fee',
    linkText: 'Add Payment Method',
  },
  [PlatformFeeAlertStatus.INVALID_PAYMENT_METHOD]: {
    title: 'Past Due Platform Fee Payment',
    description:
      'The payment method on file is invalid. Update it to resolve the past due balance and stay up to date with monthly payments.',
    linkText: 'Update & Pay',
  },
  [PlatformFeeAlertStatus.PAST_DUE_BALANCE]: {
    title: 'Past Due Platform Fee Payment',
    description: 'You have a past due balance, resolve it by paying now',
    linkText: 'Pay now',
  },
  [PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE]: {
    title: 'Past Due Platform Fee Payment',
    description:
      'Add a payment method to resolve your balance, and to stay up to date with the monthly payments',
    linkText: 'Add & Pay',
  },
  [PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES]: {
    title: 'Multiple Past Due Invoices',
    description:
      'You have multiple past due invoices, resolve them by paying now',
    linkText: 'Pay now',
  },
};

/** Alert is shown for everyone; takeover (redirect) is hidden for users with LEAD or AGENT role. */
export function PlatformFeeAlert() {
  const { data: userinfo } = useIdentity('/auth/userinfo');
  // Partners have LEAD and Agent role; Associates have AGENT only. Takeover shown only to Associates (!hasLeadTeamMember).
  const userHasLeadRole = userinfo?.teams?.some((team) =>
    team.roles?.some((role) => role === 'LEAD'),
  );
  const hasLeadTeamMember = userHasLeadRole ?? true;
  const { status, oldestPastDueInvoiceDueDate, pastDueBalance } =
    useFetchPlatformFee(hasLeadTeamMember);
  const trackPlatformFeeEvent = useTrackPlatformFeeAnalytics();
  const router = useRouter();
  const { shouldShowTakeover, setDismissedTakeoverAt } = usePlatformFeeTakeover(
    {
      status,
      oldestPastDueInvoiceDueDate,
      pastDueBalance,
      hasLeadTeamMember,
    },
  );
  const wasMounted = useRef(false);

  useEffect(() => {
    if (shouldShowTakeover) {
      router.replace(`/platform-fee?r=${window.location.pathname}`);
    }
  }, [router, shouldShowTakeover]);

  useEffect(() => {
    if (!wasMounted.current && status) {
      trackPlatformFeeEvent(TrackPlatformFeeEvent.PaymentMethodAlertMount, {
        issueType: status,
      });
      wasMounted.current = true;
    }
  }, [status, trackPlatformFeeEvent]);

  const [{ loading }, navigate] = useAsyncFn(async () => {
    const url = await getPaymentCenterUrl(status);

    if (!url) {
      const errorMessage = 'Failed to get payment center url';
      toast('error', errorMessage);
      datadogRum.addError(errorMessage);
      return;
    }

    trackPlatformFeeEvent(
      TrackPlatformFeeEvent.PaymentMethodAlertCtaButtonClick,
      {
        issueType: status,
      },
    );
    setDismissedTakeoverAt(Date.now());
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [status, trackPlatformFeeEvent, setDismissedTakeoverAt]);

  if (!status || !PLATFORM_FEE_ALERT_CONTENTS[status]) {
    return null;
  }

  return (
    <Alert
      severity="error"
      variant="stacked"
      action={
        <AlertButton
          severity="error"
          variant="contained"
          loading={loading}
          onClick={() => void navigate()}
        >
          {PLATFORM_FEE_ALERT_CONTENTS[status].linkText}
        </AlertButton>
      }
      title={PLATFORM_FEE_ALERT_CONTENTS[status].title}
      sx={{
        borderRadius: 0,
      }}
    >
      {PLATFORM_FEE_ALERT_CONTENTS[status].description}
    </Alert>
  );
}
